/**
 * Modules listing page.
 * Shows all available life domain modules with their current status.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchModules } from '../utils/api';

interface Module {
  id: string;
  slug: string;
  name: string;
  version: string;
  status: string;
  config: Record<string, unknown> | null;
}

export default function Modules() {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadModules() {
      try {
        const data = await fetchModules();
        setModules(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load modules');
      } finally {
        setLoading(false);
      }
    }

    loadModules();
  }, []);

  if (loading) {
    return (
      <div className="page-container">
        <p className="loading-text">Loading modules...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <p className="error-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <h1 className="page-title">Life Modules</h1>
        <p className="page-subtitle">
          Organize different aspects of your life in focused modules
        </p>
      </header>

      <div className="modules-grid">
        {modules.map((module) => (
          <Link
            key={module.id}
            to={`/modules/${module.slug}`}
            className="module-card"
          >
            <div className="module-header">
              <h3 className="module-name">{module.name}</h3>
              <span className={`module-status status-${module.status}`}>
                {module.status}
              </span>
            </div>
            <p className="module-version">Version {module.version}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
